import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  RefreshCw,
  DollarSign,
  Users as UsersIcon,
  TrendingUp,
  TrendingDown,
  Activity,
  AlertTriangle,
  Zap,
  Tag as TagIcon,
  LayoutGrid,
  Filter,
  Download,
  X,
  ChevronRight,
  Search,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import api from '../../api';
import toast from 'react-hot-toast';
import InfrastructureCostsWidget from './InfrastructureCostsWidget';

/**
 * Admin → AI Configuration → Usage & Cost Dashboard
 *
 * Single scrollable screen (Phase 2 spec) with:
 *   - Filter bar  (date, user, module, client tag)
 *   - KPI strip
 *   - Adoption heatmap (users × modules)
 *   - Leaderboard table (per-user, sortable)
 *   - Cost timeline (stack-by toggle)
 *   - Cost by module + by client (side-by-side summaries)
 *   - Anomaly panel
 *   - User drill-down modal
 *   - CSV export
 *
 * All numbers come from /api/admin/usage-dashboard/* which reads
 * v_usage_events_with_cost so cost attribution stays correct as pricing
 * rows change.
 */

// ─── Types ─────────────────────────────────────────────────────────────────

interface KPIs {
  active_users: number;
  active_users_prior: number;
  active_users_change: number | null;
  total_cost_usd: number;
  total_cost_usd_prior: number;
  cost_change: number | null;
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  total_events: number;
  top_cost_driver: { user_id: number; label: string; cost_usd: number } | null;
  dead_modules: string[];
  wau_sparkline: { week: string; active_users: number }[];
}

interface LeaderboardRow {
  user_id: number;
  label: string;
  email: string;
  last_active: string | null;
  days_since_active: number | null;
  status: 'active' | 'lapsed' | 'never';
  sessions: number;
  modules_touched: number;
  artifacts: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_usd: number;
  cost_usd_prior: number;
  cost_change: number | null;
}

interface ModuleRow {
  module: string;
  events: number;
  active_users: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_usd: number;
  cost_usd_prior: number;
  cost_change: number | null;
}

interface ClientRow {
  client_tag_id: number | null;
  tag_name: string;
  tag_type: string | null;
  events: number;
  active_users: number;
  cost_usd: number;
  cost_usd_prior: number;
  cost_change: number | null;
}

interface TimelinePoint {
  day: string;
  bucket: string;
  label: string;
  cost_usd: number;
  events: number;
}

interface HeatmapCell {
  user_id: number;
  user_label: string;
  module: string;
  events: number;
  cost_usd: number;
}

interface Anomalies {
  threshold: number;
  user_anomalies: { user_id: number; label: string; cost_usd: number; cost_usd_prior: number; change: number }[];
  module_anomalies: { module: string; cost_usd: number; cost_usd_prior: number; cost_change: number; events: number; events_prior: number; events_change: number | null }[];
  collapsed_modules: { module: string; cost_usd_prior: number; cost_usd: number; change: number }[];
}

interface BootstrapData {
  users: { id: number; label: string; email: string }[];
  modules: string[];
  /** Canonical list of all known modules — includes those with zero usage. */
  modules_all?: string[];
  client_tags: { tag_id: number; tag_name: string; tag_type: string; active: boolean }[];
  defaults: { startDate: string; endDate: string };
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const CHART_COLORS = ['#1e3a8a', '#7c3aed', '#be185d', '#b45309', '#047857', '#0e7490', '#b91c1c', '#4338ca', '#4d7c0f', '#c2410c'];

function formatUsd(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v.toFixed(2)}`;
}

function formatNumber(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(v);
}

function formatPct(v: number | null): { text: string; positive: boolean; negative: boolean } {
  if (v === null || !Number.isFinite(v)) return { text: '—', positive: false, negative: false };
  const pct = v * 100;
  const text = `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
  return { text, positive: pct > 0, negative: pct < 0 };
}

function priorMonthSamePeriod(startDate: string, endDate: string) {
  const s = new Date(startDate);
  const e = new Date(endDate);
  const priorStart = new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth() - 1, s.getUTCDate()));
  const priorEnd = new Date(Date.UTC(e.getUTCFullYear(), e.getUTCMonth() - 1, e.getUTCDate()));
  return {
    compareStartDate: priorStart.toISOString().slice(0, 10),
    compareEndDate: priorEnd.toISOString().slice(0, 10),
  };
}

function firstOfMonthISO() {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().slice(0, 10);
}
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

// ─── Main component ───────────────────────────────────────────────────────

const UsageDashboard: React.FC = () => {
  // Filter state
  const [startDate, setStartDate] = useState<string>(firstOfMonthISO());
  const [endDate, setEndDate] = useState<string>(todayISO());
  const [userIds, setUserIds] = useState<number[]>([]);
  const [modulesFilter, setModulesFilter] = useState<string[]>([]);
  const [clientTagIds, setClientTagIds] = useState<number[]>([]);

  // Data state
  const [bootstrap, setBootstrap] = useState<BootstrapData | null>(null);
  const [kpis, setKpis] = useState<KPIs | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardRow[]>([]);
  const [modules, setModules] = useState<ModuleRow[]>([]);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [timeline, setTimeline] = useState<TimelinePoint[]>([]);
  const [heatmap, setHeatmap] = useState<HeatmapCell[]>([]);
  const [anomalies, setAnomalies] = useState<Anomalies | null>(null);

  const [loading, setLoading] = useState(true);
  const [stackBy, setStackBy] = useState<'user' | 'module' | 'client'>('module');
  const [sortBy, setSortBy] = useState<keyof LeaderboardRow>('cost_usd');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  // Leaderboard-local filters
  const [lbSearch, setLbSearch] = useState('');
  const [lbStatus, setLbStatus] = useState<'all' | 'active' | 'lapsed' | 'never'>('all');
  const [lbMinCost, setLbMinCost] = useState<'all' | 'nonzero'>('all');

  const filterQuery = useMemo(() => {
    const params = new URLSearchParams();
    params.set('startDate', startDate);
    params.set('endDate', endDate);
    const cmp = priorMonthSamePeriod(startDate, endDate);
    params.set('compareStartDate', cmp.compareStartDate);
    params.set('compareEndDate', cmp.compareEndDate);
    if (userIds.length) params.set('userIds', userIds.join(','));
    if (modulesFilter.length) params.set('modules', modulesFilter.join(','));
    if (clientTagIds.length) params.set('clientTagIds', clientTagIds.join(','));
    return params.toString();
  }, [startDate, endDate, userIds, modulesFilter, clientTagIds]);

  // Bootstrap once
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/admin/usage-dashboard/filters/bootstrap');
        if (res.data && res.data.success) setBootstrap(res.data);
      } catch (err: any) {
        toast.error(err?.response?.data?.error || err?.message || 'Failed to load filter data');
      }
    })();
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [kpisR, lbR, modR, cliR, tlR, hmR, anomR] = await Promise.all([
        api.get(`/api/admin/usage-dashboard/kpis?${filterQuery}`),
        api.get(`/api/admin/usage-dashboard/leaderboard?${filterQuery}`),
        api.get(`/api/admin/usage-dashboard/by-module?${filterQuery}`),
        api.get(`/api/admin/usage-dashboard/by-client?${filterQuery}`),
        api.get(`/api/admin/usage-dashboard/timeline?${filterQuery}&stackBy=${stackBy}`),
        api.get(`/api/admin/usage-dashboard/heatmap?${filterQuery}`),
        api.get(`/api/admin/usage-dashboard/anomalies?${filterQuery}`),
      ]);
      if (kpisR.data?.success) setKpis(kpisR.data.kpis);
      if (lbR.data?.success) setLeaderboard(lbR.data.leaderboard || []);
      if (modR.data?.success) setModules(modR.data.modules || []);
      if (cliR.data?.success) setClients(cliR.data.clients || []);
      if (tlR.data?.success) setTimeline(tlR.data.series || []);
      if (hmR.data?.success) setHeatmap(hmR.data.cells || []);
      if (anomR.data?.success) setAnomalies(anomR.data);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [filterQuery, stackBy]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Filter + sort leaderboard client-side
  const sortedLeaderboard = useMemo(() => {
    const term = lbSearch.trim().toLowerCase();
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...leaderboard]
      .filter((r) => {
        if (lbStatus !== 'all' && r.status !== lbStatus) return false;
        if (lbMinCost === 'nonzero' && r.cost_usd <= 0) return false;
        if (!term) return true;
        return (
          (r.label || '').toLowerCase().includes(term) ||
          (r.email || '').toLowerCase().includes(term)
        );
      })
      .sort((a, b) => {
        const av = a[sortBy] ?? (typeof b[sortBy] === 'number' ? -Infinity : '');
        const bv = b[sortBy] ?? (typeof a[sortBy] === 'number' ? -Infinity : '');
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
        return String(av).localeCompare(String(bv)) * dir;
      });
  }, [leaderboard, sortBy, sortDir, lbSearch, lbStatus, lbMinCost]);

  const toggleSort = (col: keyof LeaderboardRow) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('desc'); }
  };

  const handleExportCsv = () => {
    const url = `/api/admin/usage-dashboard/export.csv?${filterQuery}`;
    window.open(url, '_blank');
  };

  // Timeline data pivoted for recharts. We fill missing days across the
  // selected range and zero out missing buckets per row so stacked areas
  // render as a continuous band rather than abruptly cutting off where
  // a single bucket happens to have no events.
  const timelinePivoted = useMemo(() => {
    const byDay: Record<string, any> = {};
    const bucketLabels = new Map<string, string>();
    for (const p of timeline) {
      const dayKey = new Date(p.day).toISOString().slice(0, 10);
      if (!byDay[dayKey]) byDay[dayKey] = { day: dayKey };
      byDay[dayKey][p.label] = (byDay[dayKey][p.label] || 0) + p.cost_usd;
      bucketLabels.set(p.label, p.label);
    }
    const buckets = Array.from(bucketLabels.keys());
    const start = new Date(`${startDate}T00:00:00Z`);
    const end = new Date(`${endDate}T00:00:00Z`);
    const data: any[] = [];
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start <= end) {
      const cursor = new Date(start);
      while (cursor <= end) {
        const k = cursor.toISOString().slice(0, 10);
        const row: any = byDay[k] || { day: k };
        for (const b of buckets) if (row[b] === undefined) row[b] = 0;
        data.push(row);
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }
    } else {
      for (const row of Object.values(byDay).sort((a: any, b: any) => a.day.localeCompare(b.day))) {
        for (const b of buckets) if ((row as any)[b] === undefined) (row as any)[b] = 0;
        data.push(row);
      }
    }
    return { data, buckets };
  }, [timeline, startDate, endDate]);

  // Heatmap pivoted. "Other" is the default module tag for any LLM
  // call where the callsite didn't supply an explicit module — render
  // it with a clearer label so it's not mistaken for a bucket name.
  const heatmapGrid = useMemo(() => {
    const users = new Map<number, { user_id: number; label: string; total: number }>();
    const modulesSet = new Set<string>();
    const cellMap = new Map<string, HeatmapCell>();
    for (const c of heatmap) {
      modulesSet.add(c.module);
      const existing = users.get(c.user_id) || { user_id: c.user_id, label: c.user_label, total: 0 };
      existing.total += c.cost_usd;
      users.set(c.user_id, existing);
      cellMap.set(`${c.user_id}::${c.module}`, c);
    }
    // Always include every canonical module from bootstrap.modules_all so
    // SOP Builder, Verification, Smart Start etc. show up even when zero
    // usage in the current window. Empty columns render as blank cells.
    const canonicalAll: string[] = (bootstrap?.modules_all && bootstrap.modules_all.length > 0)
      ? bootstrap.modules_all
      : (bootstrap?.modules || []);
    for (const m of canonicalAll) modulesSet.add(m);

    const userList = Array.from(users.values()).sort((a, b) => b.total - a.total);
    // Sort module columns: named modules A-Z first, "Other" last.
    const moduleList = Array.from(modulesSet).sort((a, b) => {
      if (a === 'Other' && b !== 'Other') return 1;
      if (b === 'Other' && a !== 'Other') return -1;
      return a.localeCompare(b);
    });
    const maxCost = Math.max(...heatmap.map((c) => c.cost_usd), 0.01);
    return { userList, moduleList, cellMap, maxCost };
  }, [heatmap, bootstrap]);

  // Convert a 0..1 intensity to a green→amber→red heat color. Low
  // activity = cool green, high activity = warm red so cost spikes
  // are visually obvious at a glance.
  const heatColor = (intensity: number): string => {
    if (intensity <= 0) return '#f9fafb';
    // Three stops: emerald → amber → red. Interpolate HSL for smoothness.
    const stops = [
      { t: 0.0, h: 160, s: 60, l: 88 }, // emerald-100
      { t: 0.5, h: 40,  s: 90, l: 70 }, // amber-ish
      { t: 1.0, h: 0,   s: 75, l: 55 }, // rose
    ];
    let a = stops[0], b = stops[1];
    for (let i = 0; i < stops.length - 1; i++) {
      if (intensity >= stops[i].t && intensity <= stops[i + 1].t) {
        a = stops[i]; b = stops[i + 1];
        break;
      }
    }
    const k = (intensity - a.t) / (b.t - a.t);
    const h = a.h + (b.h - a.h) * k;
    const s = a.s + (b.s - a.s) * k;
    const l = a.l + (b.l - a.l) * k;
    return `hsl(${h.toFixed(0)} ${s.toFixed(0)}% ${l.toFixed(0)}%)`;
  };

  const prettyModuleLabel = (m: string) => (m === 'Other' ? 'Untagged' : m);

  return (
    <div className="space-y-6">
      {/* Header + filter bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Activity className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Usage &amp; Cost Dashboard</h2>
            </div>
            <p className="text-gray-600 mt-1 max-w-3xl">
              Adoption, module utilization, and cost attribution across every Jarvis user. Default view is
              month-to-date vs. the same period last month.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <Filter className="w-4 h-4" />
              Filters {(userIds.length + modulesFilter.length + clientTagIds.length) > 0 && `(${userIds.length + modulesFilter.length + clientTagIds.length})`}
            </button>
            <button
              onClick={fetchAll}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleExportCsv}
              className="flex items-center gap-2 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
          </div>
          <div className="md:col-span-2 flex items-end text-xs text-gray-500">
            Comparing to {priorMonthSamePeriod(startDate, endDate).compareStartDate} → {priorMonthSamePeriod(startDate, endDate).compareEndDate}
          </div>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiTile
          icon={<UsersIcon className="w-4 h-4 text-blue-600" />}
          label="Active users"
          value={kpis ? String(kpis.active_users) : '—'}
          change={kpis ? kpis.active_users_change : null}
        />
        <KpiTile
          icon={<DollarSign className="w-4 h-4 text-green-600" />}
          label="Total spend"
          value={kpis ? formatUsd(kpis.total_cost_usd) : '—'}
          change={kpis ? kpis.cost_change : null}
        />
        <KpiTile
          icon={<Zap className="w-4 h-4 text-purple-600" />}
          label="Total tokens"
          value={kpis ? formatNumber(kpis.total_tokens) : '—'}
        />
        <KpiTile
          icon={<Activity className="w-4 h-4 text-indigo-600" />}
          label="LLM calls"
          value={kpis ? formatNumber(kpis.total_events) : '—'}
        />
        <KpiTile
          icon={<TrendingUp className="w-4 h-4 text-amber-600" />}
          label="Top cost driver"
          value={kpis?.top_cost_driver ? kpis.top_cost_driver.label : '—'}
          sub={kpis?.top_cost_driver ? formatUsd(kpis.top_cost_driver.cost_usd) : undefined}
        />
        <KpiTile
          icon={<AlertTriangle className="w-4 h-4 text-red-600" />}
          label="Dead modules"
          value={kpis ? String(kpis.dead_modules.length) : '—'}
          sub={kpis && kpis.dead_modules.length ? kpis.dead_modules.slice(0, 2).join(', ') : undefined}
        />
      </div>

      {/* Infrastructure costs + total spend (variable + fixed) */}
      <InfrastructureCostsWidget startDate={startDate} endDate={endDate} />

      {/* Cost Timeline */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Cost timeline</h3>
            <p className="text-sm text-gray-600">Daily spend, stacked by your selected bucket.</p>
          </div>
          <div className="inline-flex rounded-lg border border-gray-300 p-0.5">
            {(['module', 'user', 'client'] as const).map((opt) => (
              <button
                key={opt}
                onClick={() => setStackBy(opt)}
                className={`px-3 py-1 text-xs font-medium rounded-md ${stackBy === opt ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                {opt.charAt(0).toUpperCase() + opt.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div style={{ width: '100%', height: 320 }}>
          <ResponsiveContainer>
            <AreaChart data={timelinePivoted.data} margin={{ top: 10, right: 24, left: 8, bottom: 0 }}>
              <defs>
                {timelinePivoted.buckets.map((b, idx) => {
                  const color = CHART_COLORS[idx % CHART_COLORS.length];
                  return (
                    <linearGradient key={b} id={`tlGrad-${idx}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.85} />
                      <stop offset="100%" stopColor={color} stopOpacity={0.25} />
                    </linearGradient>
                  );
                })}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 11, fill: '#475569' }}
                tickLine={false}
                axisLine={{ stroke: '#cbd5e1' }}
                minTickGap={24}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#475569' }}
                tickLine={false}
                axisLine={{ stroke: '#cbd5e1' }}
                tickFormatter={(v) => formatUsd(v)}
                width={64}
              />
              <Tooltip
                formatter={(v: any) => formatUsd(Number(v))}
                contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 8, color: '#f8fafc', fontSize: 12 }}
                itemStyle={{ color: '#f8fafc' }}
                labelStyle={{ color: '#cbd5e1', fontWeight: 600 }}
                cursor={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '3 3' }}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" />
              {timelinePivoted.buckets.map((b, idx) => (
                <Area
                  key={b}
                  type="monotone"
                  dataKey={b}
                  stackId="1"
                  stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                  strokeWidth={1.5}
                  fill={`url(#tlGrad-${idx})`}
                  activeDot={{ r: 3, strokeWidth: 0 }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Adoption heatmap */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">Adoption heatmap</h3>
            <span className="text-sm text-gray-500">Users × modules. Green = light use, red = power user / spike.</span>
          </div>
          {/* Color legend */}
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span>Low</span>
            <div className="flex h-3 w-28 rounded overflow-hidden border border-gray-200">
              {Array.from({ length: 20 }).map((_, i) => (
                <div key={i} className="flex-1" style={{ backgroundColor: heatColor(i / 19) }} />
              ))}
            </div>
            <span>High</span>
          </div>
        </div>
        {heatmapGrid.userList.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 text-center">No activity in this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr>
                  <th className="text-left p-2 sticky left-0 bg-white font-semibold">User</th>
                  {heatmapGrid.moduleList.map((m) => (
                    <th
                      key={m}
                      className={`p-2 text-left font-semibold ${m === 'Other' ? 'text-gray-400 italic' : ''}`}
                      title={m === 'Other' ? 'Calls where the module tag was not set at capture time' : undefined}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {prettyModuleLabel(m)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {heatmapGrid.userList.map((u) => (
                  <tr key={u.user_id}>
                    <td className="p-2 sticky left-0 bg-white font-medium text-gray-900" style={{ whiteSpace: 'nowrap' }}>{u.label}</td>
                    {heatmapGrid.moduleList.map((m) => {
                      const cell = heatmapGrid.cellMap.get(`${u.user_id}::${m}`);
                      const intensity = cell ? Math.min(1, cell.cost_usd / heatmapGrid.maxCost) : 0;
                      const bg = heatColor(intensity);
                      // Darker text on darker cells for contrast.
                      const textClass = intensity > 0.6 ? 'text-gray-900 font-semibold' : 'text-gray-700';
                      return (
                        <td
                          key={m}
                          className={`p-2 text-center ${textClass}`}
                          title={cell ? `${cell.events} call${cell.events === 1 ? '' : 's'} · ${formatUsd(cell.cost_usd)}` : 'No activity'}
                          style={{ backgroundColor: bg, minWidth: 64, cursor: cell ? 'pointer' : 'default' }}
                          onClick={() => cell && setSelectedUserId(u.user_id)}
                        >
                          {cell ? formatUsd(cell.cost_usd) : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-2 text-xs text-gray-500">
          <span className="italic">Untagged</span> column = LLM calls where the module wasn't explicitly tagged at capture time.
          Instrumenting the callsite with a <code className="bg-gray-100 px-1 rounded">__usage.module</code> hint moves those calls into the correct column.
        </p>
      </div>

      {/* Anomaly panel — exceptions before raw data */}
      {anomalies && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h3 className="text-lg font-semibold text-gray-900">Biggest MTD-vs-prior movers</h3>
            <span className="text-xs text-gray-500">Threshold: ±{(anomalies.threshold * 100).toFixed(0)}%</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <AnomalyList
              title="User spend"
              icon={<UsersIcon className="w-4 h-4" />}
              items={anomalies.user_anomalies.map((a) => ({
                label: a.label,
                now: formatUsd(a.cost_usd),
                prior: formatUsd(a.cost_usd_prior),
                change: a.change,
                onClick: () => setSelectedUserId(a.user_id),
              }))}
              empty="No user cost anomalies this period."
            />
            <AnomalyList
              title="Module spend"
              icon={<LayoutGrid className="w-4 h-4" />}
              items={anomalies.module_anomalies.map((a) => ({
                label: a.module,
                now: formatUsd(a.cost_usd),
                prior: formatUsd(a.cost_usd_prior),
                change: a.cost_change,
              }))}
              empty="No module cost anomalies."
            />
            <AnomalyList
              title="Collapsed modules"
              icon={<TrendingDown className="w-4 h-4" />}
              items={anomalies.collapsed_modules.map((a) => ({
                label: a.module,
                now: formatUsd(0),
                prior: formatUsd(a.cost_usd_prior),
                change: -1,
              }))}
              empty="No modules collapsed to zero."
            />
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Leaderboard</h3>
            <p className="text-sm text-gray-600">
              Per-user cost, tokens, and adoption. Click column headers to sort, click a row to drill into one user.
              Showing {sortedLeaderboard.length} of {leaderboard.length}.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={lbSearch}
                onChange={(e) => setLbSearch(e.target.value)}
                placeholder="Search name or email..."
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg w-56 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {lbSearch && (
                <button
                  onClick={() => setLbSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <select
              value={lbStatus}
              onChange={(e) => setLbStatus(e.target.value as any)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="lapsed">Lapsed</option>
              <option value="never">Never logged in</option>
            </select>
            <select
              value={lbMinCost}
              onChange={(e) => setLbMinCost(e.target.value as any)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All users</option>
              <option value="nonzero">Only users with activity</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <ThSortable label="User" col="label" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <ThSortable label="Status" col="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <ThSortable label="Sessions" col="sessions" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                <ThSortable label="Modules" col="modules_touched" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                <ThSortable label="Artifacts" col="artifacts" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                <ThSortable label="Input" col="input_tokens" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                <ThSortable label="Output" col="output_tokens" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                <ThSortable label="Cost" col="cost_usd" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                <ThSortable label="Δ vs prior" col="cost_change" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                <th className="px-4 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="text-center py-10"><RefreshCw className="w-5 h-5 inline animate-spin mr-2" /> Loading…</td></tr>
              )}
              {!loading && sortedLeaderboard.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center py-10 text-gray-500">
                    {leaderboard.length === 0
                      ? 'No activity in this period.'
                      : 'No users match your filters.'}
                    {(lbSearch || lbStatus !== 'all' || lbMinCost !== 'all') && leaderboard.length > 0 && (
                      <button
                        onClick={() => { setLbSearch(''); setLbStatus('all'); setLbMinCost('all'); }}
                        className="ml-2 text-blue-600 hover:text-blue-800 underline"
                      >
                        Clear filters
                      </button>
                    )}
                  </td>
                </tr>
              )}
              {sortedLeaderboard.map((r) => {
                const change = formatPct(r.cost_change);
                return (
                  <tr
                    key={r.user_id}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedUserId(r.user_id)}
                  >
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900">{r.label}</div>
                      <div className="text-xs text-gray-500">{r.email}</div>
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge status={r.status} daysSince={r.days_since_active} />
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{r.sessions}</td>
                    <td className="px-4 py-2 text-right font-mono">{r.modules_touched}</td>
                    <td className="px-4 py-2 text-right font-mono">{r.artifacts}</td>
                    <td className="px-4 py-2 text-right font-mono text-gray-600">{formatNumber(r.input_tokens)}</td>
                    <td className="px-4 py-2 text-right font-mono text-gray-600">{formatNumber(r.output_tokens)}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold text-gray-900">{formatUsd(r.cost_usd)}</td>
                    <td className={`px-4 py-2 text-right font-mono ${change.positive ? 'text-red-600' : change.negative ? 'text-green-600' : 'text-gray-400'}`}>
                      {change.text}
                    </td>
                    <td className="px-2 text-gray-400"><ChevronRight className="w-4 h-4" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* By module + by client side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SummaryCard
          title="By module"
          subtitle="Which modules earn their keep"
          rows={modules.map((m) => ({
            key: m.module,
            label: m.module,
            meta: `${m.active_users} user${m.active_users === 1 ? '' : 's'} · ${formatNumber(m.events)} calls`,
            cost: m.cost_usd,
            change: m.cost_change,
          }))}
        />
        <SummaryCard
          title="By client engagement"
          subtitle="Billable vs. internal/R&D split"
          rows={clients.map((c) => ({
            key: String(c.client_tag_id),
            label: c.tag_name,
            meta: `${c.tag_type || 'untagged'} · ${c.active_users} user${c.active_users === 1 ? '' : 's'}`,
            cost: c.cost_usd,
            change: c.cost_change,
          }))}
        />
      </div>


      {selectedUserId !== null && (
        <UserDrillDown
          userId={selectedUserId}
          filterQuery={filterQuery}
          onClose={() => setSelectedUserId(null)}
        />
      )}

      {showFilters && bootstrap && (
        <FiltersModal
          bootstrap={bootstrap}
          userIds={userIds}
          modules={modulesFilter}
          clientTagIds={clientTagIds}
          onApply={(u, m, c) => { setUserIds(u); setModulesFilter(m); setClientTagIds(c); setShowFilters(false); }}
          onClose={() => setShowFilters(false)}
        />
      )}
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────

const KpiTile: React.FC<{ icon: React.ReactNode; label: string; value: string; sub?: string; change?: number | null }> = ({ icon, label, value, sub, change }) => {
  const c = change !== undefined && change !== null ? formatPct(change) : null;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <div className="flex items-center gap-1.5 text-xs text-gray-500 uppercase tracking-wide">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1 text-xl font-bold text-gray-900 truncate" title={value}>{value}</div>
      {sub && <div className="text-xs text-gray-500 truncate">{sub}</div>}
      {c && (
        <div className={`mt-1 text-xs font-medium ${c.positive ? 'text-red-600' : c.negative ? 'text-green-600' : 'text-gray-400'}`}>
          {c.positive && <TrendingUp className="inline w-3 h-3 mr-0.5" />}
          {c.negative && <TrendingDown className="inline w-3 h-3 mr-0.5" />}
          {c.text} vs prior
        </div>
      )}
    </div>
  );
};

const StatusBadge: React.FC<{ status: 'active' | 'lapsed' | 'never'; daysSince: number | null }> = ({ status, daysSince }) => {
  const cls = status === 'active' ? 'bg-green-100 text-green-800'
    : status === 'lapsed' ? 'bg-amber-100 text-amber-800'
    : 'bg-gray-200 text-gray-700';
  const text = status === 'never' ? 'Never' : status === 'lapsed' ? `Lapsed ${daysSince}d` : daysSince === null ? 'Active' : daysSince === 0 ? 'Today' : `${daysSince}d ago`;
  return <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${cls}`}>{text}</span>;
};

const ThSortable: React.FC<{
  label: string; col: keyof LeaderboardRow; sortBy: keyof LeaderboardRow; sortDir: 'asc' | 'desc'; onSort: (c: keyof LeaderboardRow) => void; align?: 'left' | 'right';
}> = ({ label, col, sortBy, sortDir, onSort, align = 'left' }) => (
  <th
    onClick={() => onSort(col)}
    className={`px-4 py-3 font-semibold text-gray-700 cursor-pointer select-none hover:bg-gray-100 text-${align}`}
  >
    {label}
    {sortBy === col && <span className="ml-1">{sortDir === 'asc' ? '▲' : '▼'}</span>}
  </th>
);

const SummaryCard: React.FC<{
  title: string; subtitle?: string;
  rows: { key: string; label: string; meta?: string; cost: number; change: number | null }[];
}> = ({ title, subtitle, rows }) => {
  const maxCost = Math.max(...rows.map((r) => r.cost), 0.01);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      {subtitle && <p className="text-sm text-gray-600 mb-3">{subtitle}</p>}
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-sm text-gray-400 py-4">No data</div>}
        {rows.map((r) => {
          const change = formatPct(r.change);
          const pct = (r.cost / maxCost) * 100;
          return (
            <div key={r.key} className="border border-gray-100 rounded-lg p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate" title={r.label}>{r.label}</div>
                  {r.meta && <div className="text-xs text-gray-500 truncate">{r.meta}</div>}
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm font-semibold text-gray-900">{formatUsd(r.cost)}</div>
                  {change.text !== '—' && (
                    <div className={`text-xs ${change.positive ? 'text-red-600' : change.negative ? 'text-green-600' : 'text-gray-400'}`}>{change.text}</div>
                  )}
                </div>
              </div>
              <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AnomalyList: React.FC<{
  title: string; icon: React.ReactNode;
  items: { label: string; now: string; prior: string; change: number; onClick?: () => void }[];
  empty: string;
}> = ({ title, icon, items, empty }) => (
  <div>
    <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 mb-2">{icon} {title}</div>
    {items.length === 0 && <div className="text-xs text-gray-400 py-2">{empty}</div>}
    <div className="space-y-1">
      {items.map((it, i) => {
        const c = formatPct(it.change);
        return (
          <button
            key={i}
            onClick={it.onClick}
            className={`w-full text-left border border-gray-100 rounded p-2 hover:bg-gray-50 ${it.onClick ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-gray-900 truncate">{it.label}</span>
              <span className={`text-xs font-semibold ${c.positive ? 'text-red-600' : 'text-green-600'}`}>{c.text}</span>
            </div>
            <div className="text-xs text-gray-500">{it.prior} → {it.now}</div>
          </button>
        );
      })}
    </div>
  </div>
);

// ─── User drill-down modal ─────────────────────────────────────────────────

const UserDrillDown: React.FC<{ userId: number; filterQuery: string; onClose: () => void }> = ({ userId, filterQuery, onClose }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/api/admin/usage-dashboard/users/${userId}?${filterQuery}`);
        if (res.data?.success) setData(res.data);
      } catch (err: any) {
        toast.error(err?.response?.data?.error || err?.message || 'Failed to load user detail');
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, filterQuery]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{data?.user?.label || 'User'} — drill-down</h3>
            {data?.user?.email && <p className="text-xs text-gray-500">{data.user.email}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          {loading && <div className="text-center py-10"><RefreshCw className="w-5 h-5 inline animate-spin mr-2" /> Loading…</div>}
          {!loading && data && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <KpiTile icon={<DollarSign className="w-4 h-4 text-green-600" />} label="Total spend" value={formatUsd(data.totals.cost_usd)} />
                <KpiTile icon={<Activity className="w-4 h-4 text-indigo-600" />} label="Containers touched" value={String(data.totals.containers)} />
                <KpiTile icon={<TrendingUp className="w-4 h-4 text-amber-600" />} label="Cost / artifact" value={data.totals.cost_per_artifact === null ? '—' : formatUsd(data.totals.cost_per_artifact)} />
              </div>

              <Section title="By module">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left p-2">Module</th>
                      <th className="text-right p-2">Calls</th>
                      <th className="text-right p-2">Input</th>
                      <th className="text-right p-2">Output</th>
                      <th className="text-right p-2">Cost</th>
                      <th className="text-right p-2">Cost / container</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_module.map((r: any) => (
                      <tr key={r.module} className="border-b border-gray-100">
                        <td className="p-2">{r.module}</td>
                        <td className="p-2 text-right font-mono">{r.events}</td>
                        <td className="p-2 text-right font-mono text-gray-600">{formatNumber(r.input_tokens)}</td>
                        <td className="p-2 text-right font-mono text-gray-600">{formatNumber(r.output_tokens)}</td>
                        <td className="p-2 text-right font-mono font-semibold">{formatUsd(r.cost_usd)}</td>
                        <td className="p-2 text-right font-mono text-gray-700">{r.cost_per_container === null ? '—' : formatUsd(r.cost_per_container)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>

              <Section title="By client engagement">
                <div className="space-y-2">
                  {data.by_client.map((c: any) => (
                    <div key={c.client_tag_id || 'null'} className="flex items-center justify-between border border-gray-100 rounded p-2 text-sm">
                      <span><TagIcon className="inline w-3.5 h-3.5 mr-1 text-gray-500" />{c.tag_name}</span>
                      <span>
                        <span className="text-gray-500 mr-3">{c.events} calls</span>
                        <span className="font-mono font-semibold">{formatUsd(c.cost_usd)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Top actions">
                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {data.by_action.map((a: any) => (
                    <div key={a.action_type} className="flex justify-between text-xs py-1 border-b border-gray-50">
                      <span className="font-mono text-gray-700">{a.action_type}</span>
                      <span className="text-gray-600">{a.events} × · {formatUsd(a.cost_usd)}</span>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Recent containers (proxy for artifacts)">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2">Module</th>
                      <th className="text-left p-2">Container</th>
                      <th className="text-left p-2">Client</th>
                      <th className="text-right p-2">Calls</th>
                      <th className="text-right p-2">Cost</th>
                      <th className="text-left p-2">Last touched</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_artifacts.map((r: any, i: number) => (
                      <tr key={i} className="border-b border-gray-50">
                        <td className="p-2">{r.module}</td>
                        <td className="p-2 font-mono">#{r.container_id}</td>
                        <td className="p-2">{r.tag_name}</td>
                        <td className="p-2 text-right font-mono">{r.events}</td>
                        <td className="p-2 text-right font-mono">{formatUsd(r.cost_usd)}</td>
                        <td className="p-2 text-gray-500">{new Date(r.last_touched).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h4 className="font-semibold text-gray-900 mb-2">{title}</h4>
    <div className="border border-gray-200 rounded-lg overflow-hidden">{children}</div>
  </div>
);

// ─── Filters modal ─────────────────────────────────────────────────────────

const FiltersModal: React.FC<{
  bootstrap: BootstrapData;
  userIds: number[];
  modules: string[];
  clientTagIds: number[];
  onApply: (u: number[], m: string[], c: number[]) => void;
  onClose: () => void;
}> = ({ bootstrap, userIds, modules, clientTagIds, onApply, onClose }) => {
  const [u, setU] = useState<number[]>(userIds);
  const [m, setM] = useState<string[]>(modules);
  const [c, setC] = useState<number[]>(clientTagIds);

  const toggle = <T,>(arr: T[], set: (n: T[]) => void, v: T) => {
    set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <h4 className="font-semibold text-gray-700 mb-2">Users ({u.length} selected)</h4>
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
              {bootstrap.users.map((x) => (
                <label key={x.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                  <input type="checkbox" checked={u.includes(x.id)} onChange={() => toggle(u, setU, x.id)} />
                  {x.label} <span className="text-gray-400 text-xs">{x.email}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-gray-700 mb-2">Modules ({m.length} selected)</h4>
            <div className="grid grid-cols-2 gap-1">
              {bootstrap.modules.map((x) => (
                <label key={x} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                  <input type="checkbox" checked={m.includes(x)} onChange={() => toggle(m, setM, x)} />
                  {x}
                </label>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-gray-700 mb-2">Client tags ({c.length} selected)</h4>
            <div className="grid grid-cols-2 gap-1">
              {bootstrap.client_tags.map((x) => (
                <label key={x.tag_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                  <input type="checkbox" checked={c.includes(x.tag_id)} onChange={() => toggle(c, setC, x.tag_id)} />
                  {x.tag_name} <span className="text-gray-400 text-xs">({x.tag_type})</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-between gap-2 px-6 py-4 border-t border-gray-200 sticky bottom-0 bg-white">
          <button onClick={() => { setU([]); setM([]); setC([]); }} className="text-sm text-gray-600 hover:text-gray-900">Clear all</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
            <button onClick={() => onApply(u, m, c)} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageDashboard;
